/**
 * BPM detection via spectral flux onset detection + autocorrelation.
 */
import { fft } from './fft';

const FFT_SIZE = 2048;
const HOP_SIZE = 512;
const MIN_BPM = 50;
const MAX_BPM = 220;

function hannWindow(size: number): Float64Array {
  const w = new Float64Array(size);
  for (let i = 0; i < size; i++) {
    w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (size - 1)));
  }
  return w;
}

function binFreqs(sampleRate: number, fftSize: number): Float64Array {
  const arr = new Float64Array(fftSize / 2 + 1);
  for (let i = 0; i < arr.length; i++) arr[i] = (i * sampleRate) / fftSize;
  return arr;
}

function computeOnsetCurve(data: Float32Array, sampleRate: number): Float64Array {
  const window = hannWindow(FFT_SIZE);
  const numFrames = Math.floor((data.length - FFT_SIZE) / HOP_SIZE) + 1;
  const onset = new Float64Array(numFrames);
  const freqs = binFreqs(sampleRate, FFT_SIZE);
  let prevMag: Float64Array | null = null;

  for (let f = 0; f < numFrames; f++) {
    const start = f * HOP_SIZE;
    const frame = new Float64Array(FFT_SIZE);
    for (let i = 0; i < FFT_SIZE; i++) {
      frame[i] = data[start + i] * window[i];
    }
    const { real, imag } = fft(frame);
    const mag = new Float64Array(FFT_SIZE / 2 + 1);
    for (let k = 0; k < mag.length; k++) {
      mag[k] = Math.sqrt(real[k] * real[k] + imag[k] * imag[k]);
    }

    if (prevMag) {
      let flux = 0;
      for (let k = 0; k < mag.length; k++) {
        const diff = mag[k] - prevMag[k];
        if (freqs[k] >= 20 && freqs[k] <= 8000 && diff > 0) {
          flux += diff;
        }
      }
      onset[f] = flux;
    }
    prevMag = mag;
  }

  // Normalize and smooth slightly
  let max = 0;
  for (let i = 0; i < onset.length; i++) if (onset[i] > max) max = onset[i];
  if (max > 0) {
    for (let i = 0; i < onset.length; i++) onset[i] /= max;
  }
  // Simple moving-average smoothing (3-tap)
  const smooth = new Float64Array(onset.length);
  for (let i = 0; i < onset.length; i++) {
    const a = onset[i - 1] ?? 0;
    const b = onset[i];
    const c = onset[i + 1] ?? 0;
    smooth[i] = (a + b + c) / 3;
  }
  return smooth;
}

/**
 * Comb-filter beat alignment score.
 * Sums onset energy at every hypothesised beat position,
 * searching over phase to find the best alignment.
 */
function scoreBpm(onset: Float64Array, bpm: number, frameRate: number): number {
  const period = (60 * frameRate) / bpm;
  const step = Math.max(1, period / 8);
  let bestScore = -Infinity;

  for (let phase = 0; phase < period; phase += step) {
    let score = 0;
    for (let t = phase; t < onset.length; t += period) {
      const idx = Math.round(t);
      if (idx >= 0 && idx < onset.length) {
        score += onset[idx];
      }
    }
    if (score > bestScore) bestScore = score;
  }

  return bestScore;
}

function getBpmPrior(bpm: number): number {
  if (bpm >= 100 && bpm <= 140) return 2.0;
  if (bpm >= 80 && bpm < 100) return 1.5;
  if (bpm > 140 && bpm <= 170) return 1.5;
  if (bpm >= 60 && bpm < 80) return 0.7;
  if (bpm > 170 && bpm <= 220) return 0.8;
  return 1.0;
}

export function detectBPM(
  mono: Float32Array,
  sampleRate: number
): { bpm: number; confidence: number } {
  const onset = computeOnsetCurve(mono, sampleRate);
  const frameRate = sampleRate / HOP_SIZE;

  // Coarse scan every integer BPM
  const rawScores: number[] = [];
  let bestCoarse = { bpm: 120, raw: 0, score: -Infinity };
  for (let bpm = MIN_BPM; bpm <= MAX_BPM; bpm++) {
    const raw = scoreBpm(onset, bpm, frameRate);
    rawScores.push(raw);
    const score = raw * getBpmPrior(bpm);
    if (score > bestCoarse.score) {
      bestCoarse = { bpm, raw, score };
    }
  }

  // Fine scan ±5 BPM around coarse winner (0.25 BPM steps)
  let bestFine = bestCoarse;
  for (let bpm = Math.max(MIN_BPM, bestCoarse.bpm - 5); bpm <= Math.min(MAX_BPM, bestCoarse.bpm + 5); bpm += 0.25) {
    const raw = scoreBpm(onset, bpm, frameRate);
    const score = raw * getBpmPrior(bpm);
    if (score > bestFine.score) {
      bestFine = { bpm, raw, score };
    }
  }

  // Octave disambiguation: compare fine winner with ×2 and ÷2
  const variants = [bestFine.bpm];
  if (bestFine.bpm * 2 <= MAX_BPM) variants.push(bestFine.bpm * 2);
  if (bestFine.bpm / 2 >= MIN_BPM) variants.push(bestFine.bpm / 2);

  let bestFinal = bestFine;
  for (const bpm of variants) {
    const raw = scoreBpm(onset, bpm, frameRate);
    const score = raw * getBpmPrior(bpm);
    if (score > bestFinal.score) {
      bestFinal = { bpm, raw, score };
    }
  }

  // Confidence = how much the winner stands out vs the average wrong BPM
  const meanRaw = rawScores.reduce((a, b) => a + b, 0) / rawScores.length;
  const confidence = Math.min(1, Math.max(0, (bestFinal.raw - meanRaw) / (bestFinal.raw + 1e-9)));

  return { bpm: Math.round(bestFinal.bpm), confidence: Math.round(confidence * 100) / 100 };
}
